import React from "react";
import ImageCarousel from "./ImageCarousel";
import classNames from "classnames";
import CollapsedPackageRow from "./CollapsedPackageRow";
import "./PackageCard.css";
import { isAdminUser } from "../../../../services/authService";
import "./PackageTable.scss";
import { convertPrice } from "../../../../services/currencyService";
import LocationMap from "../../../../Pages/Client/HotelProfile/Components/LocationMap/locationmap";
import { GoogleMapSecretKey } from "../../../../assets/GlobalVariables";
import { useTranslation } from "react-i18next";
import hotelSVG from "../../../../assets/images/card/hotel.svg";
import arrowDownSVG from "../../../../assets/images/card/arrow-down.svg";

const DatesCellRender = ({ checkIn, checkOut }) => {
  return (
    <div>
      {checkIn} - {checkOut}
    </div>
  );
};

const HotelName = ({ name }) => {
  return <div className="blue-name-cell">{name}</div>;
};

const SumPaid = ({ sum }) => {
  return (
    <div className={classNames("sum-paid", { positive: sum > 0 })}>
      {convertPrice(sum || 0)}
    </div>
  );
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear();
  const formattedDate = `${day}/${month}/${year}`;

  return formattedDate;
};

const DynamicCard = ({ row, index, cancelOrder, orders, setOrders } = {}) => {
  const translate = useTranslation();

  const isAdmin = isAdminUser();
  // console.log("PackageRow", row);

  const [hotelImage, setHotelImage] = React.useState(null);
  const [hotelLongLat, setHotelLongLat] = React.useState(null);
  const [isOpen, setIsOpen] = React.useState(false);
  // Removed unused hotel/flights state, now using row.hotel/row.flights directly

  // Extract new structure fields
  const {
    agent = {},
    check_in,
    check_out,
    created_at,
    flight_booked,
    hotel_booked,
    supplement = {},
    payments = [],
    ourprice = {},
    sumpaid = 0,
    commission = 0,
    handlingfee = 0,
    paid = false,
    pending = false,
    cancelled = false,
    bank_transfer = false,
    order_response = null,
  } = row || {};

  // Use hotel and flights from row directly
  const hotel = row.hotel || {};
  const flights = row.flights || {};

  // Agent info
  const agentName = agent.firstname || "";
  const agentLastName = agent.lastname || "";
  const agentEmail = agent.email || "";
  const agentCell = agent.cell || "";
  const agentLogo = agent.companylogobase65 || null;

  // Hotel info
  const hotelName = hotel.hotelname || hotel.hotelName || hotel.fullname || "";
  const hotelAddress = hotel.hoteladdress || hotel.address || "";
  const hotelRooms = hotel.rooms || [];

  // Flight info
  const flightInfo = flights.linkedflights?.flight || [];
  const flightDep = flightInfo[0]?.leg || {};
  const flightRet = flightInfo[1]?.leg || {};

  // Supplements
  const supplementsArr = Object.values(supplement).filter((s) => s.checked);

  // Payment info
  const totalToPay =
    ourprice.ourTotalFare?.priceWithCommissionAndCreditCardFee || 0;
  const sumPaid = parseFloat(sumpaid) || 0;
  const leftToPay = totalToPay - sumPaid;

  async function getGeolocation(address) {
    try {
      // const language = localStorage.getItem('i18nextLng') ? localStorage.getItem('i18nextLng') : 'he';
      const language = "en";

      const encodedAddress = encodeURIComponent(address);
      const apiUrl = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodedAddress}&key=${GoogleMapSecretKey}&language=${language}`;

      const response = await fetch(apiUrl);
      const data = await response.json();

      if (data.status === "OK" && data.results.length > 0) {
        const result = data.results[0];
        const country = result.address_components.find((component) =>
          component.types.includes("country")
        ).long_name;

        const location = result.geometry.location;
        const latitude = location.lat;
        const longitude = location.lng;

        return { country, latitude, longitude };
      } else {
        throw new Error("Unable to retrieve geolocation data");
      }
    } catch (error) {
      console.error("Error fetching geolocation:", error.message);
      throw error;
    }
  }

  React.useEffect(() => {
    if (!order_response || !order_response.reservation) return;
    const { hotel, flights } = order_response.reservation;
    if (hotel) {
      const { resourceUrl, location } = hotel;
      const fetchData = async () => {
        try {
          if (resourceUrl) {
            setHotelImage(resourceUrl);
          }

          if (location) {
            const result = await getGeolocation(location);
            setHotelLongLat({ lat: result.latitude, lng: result.longitude });
          }
        } catch (error) {
          console.error("Error:", error.message);
        }
      };
      fetchData();
    }
  }, [order_response]);

  // Helper to display boolean as Yes/No
  const boolDisplay = (val) =>
    val === true ? "Yes" : val === false ? "No" : "-";

  // Helper to display JSON or string
  const displayValue = (val) => {
    if (val === undefined || val === null) return "-";
    if (typeof val === "object") return JSON.stringify(val);
    return val.toString();
  };

  return (
    <React.Fragment>
      <div className="card">
        <div
          className={
            "order-status" +
            (cancelled ? " cancelled" : paid ? " paid" : " pending")
          }
        >
          {cancelled
            ? "בוטלה"
            : pending
              ? "ממתינה לאישור ספק"
              : paid
                ? "שולמה"
                : bank_transfer
                  ? "ממתינה להעברה בנקאית"
                  : "ממתינה לתשלום"}
        </div>
        <div className="image-and-map">
          {/* Carousel for hotel images */}
          {hotelRooms[0]?.images && hotelRooms[0].images.length > 0 ? (
            <ImageCarousel
              images={hotelRooms[0].images}
              hotelName={hotelName}
            />
          ) : (
            <img className="image" src={hotelImage} alt={hotelName} />
          )}
          {hotelLongLat != null ? (
            <div className="map">
              <LocationMap
                lat={hotelLongLat.lat}
                lng={hotelLongLat.lng}
                mapId={"map-" + index}
              />
            </div>
          ) : null}
        </div>

        {/* Agent Info */}
        {/* <div
          className="dynamiccard-data"
          style={{
            padding: "12px",
            background: "#f7f7f7",
            borderRadius: "8px",
            margin: "12px 0",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {agentLogo && (
              <img
                src={agentLogo}
                alt="logo"
                style={{ width: 40, height: 40, borderRadius: "50%" }}
              />
            )}
            <div>
              <b>Agent:</b> {agentName} {agentLastName}
              <br />
              <b>Email:</b> {agentEmail}
              <br />
              <b>Phone:</b> {agentCell}
            </div>
          </div>
        </div> */}

        {/* Hotel Info */}
        {/* <div
          className="dynamiccard-data"
          style={{
            padding: "12px",
            background: "#f7f7f7",
            borderRadius: "8px",
            margin: "12px 0",
          }}
        >
          <div>
            <b>Hotel:</b> {hotelName}
          </div>
          <div>
            <b>Address:</b> {hotelAddress}
          </div>
          <div>
            <b>Check-in:</b> {check_in}
          </div>
          <div>
            <b>Check-out:</b> {check_out}
          </div>
          <div>
            <b>Room:</b>{" "}
            {hotelRooms[0]?.roomname || hotelRooms[0]?.roomType || "-"}
          </div>
        </div> */}

        {/* Flight Info */}
        {/* <div
          className="dynamiccard-data"
          style={{
            padding: "12px",
            background: "#f7f7f7",
            borderRadius: "8px",
            margin: "12px 0",
          }}
        >
          <div>
            <b>Flight Out:</b> {flightDep.airlineId} {flightDep.flightNumber}{" "}
            {flightDep.depCity} → {flightDep.arrCity} {flightDep.depDate}
          </div>
          <div>
            <b>Flight Back:</b> {flightRet.airlineId} {flightRet.flightNumber}{" "}
            {flightRet.depCity} → {flightRet.arrCity} {flightRet.depDate}
          </div>
        </div> */}

        {/* Supplements */}
        {/* {supplementsArr.length > 0 && (
          <div
            className="dynamiccard-data"
            style={{
              padding: "12px",
              background: "#f7f7f7",
              borderRadius: "8px",
              margin: "12px 0",
            }}
          >
            <b>Supplements:</b>
            <ul>
              {supplementsArr.map((supp, i) => (
                <li key={i}>
                  {supp.type} - {supp.description} ({supp.weightText})
                </li>
              ))}
            </ul>
          </div>
        )} */}

        {/* Payment Info */}
        {/* <div
          className="dynamiccard-data"
          style={{
            padding: "12px",
            background: "#f7f7f7",
            borderRadius: "8px",
            margin: "12px 0",
          }}
        >
          <div>
            <b>Total to pay:</b> {convertPrice(totalToPay)}
          </div>
          <div>
            <b>Paid:</b> {convertPrice(sumPaid)}
          </div>
          <div>
            <b>Left to pay:</b> {convertPrice(leftToPay)}
          </div>
          <div>
            <b>Commission:</b> {convertPrice(commission)}
          </div>
          <div>
            <b>Handling Fee:</b>{" "}
            {handlingfee ? convertPrice(handlingfee) : "אין"}
          </div>
        </div> */}

        <div
          className={"info-open-card" + (isOpen ? " open" : "")}
          onClick={() => {
            setIsOpen(!isOpen);
          }}
        >
          <div className="info">
            <div className="icon-name-date">
              <img className="icon" src={hotelSVG} alt="hotel" />
              <div className="name-and-dates">
                <div className="name">
                  <HotelName name={hotelName} />
                </div>
                <div className="dates">
                  <DatesCellRender checkIn={check_in} checkOut={check_out} />
                </div>
              </div>
            </div>
          </div>
          <div className="open-text">
            <div className="text">
              {isOpen
                ? translate.t("hotelcard.closecard")
                : translate.t("hotelcard.opencard")}
            </div>
            <img className="icon" src={arrowDownSVG} alt="open/close" />
          </div>
        </div>
        {isOpen ? (
          <CollapsedPackageRow
            orders={orders}
            setOrders={setOrders}
            cancelOrder={cancelOrder}
            row={row}
            index={index}
          />
        ) : null}
      </div>
    </React.Fragment>
  );
};

export default DynamicCard;
